import React, { useState, useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  useRetrieveProjects,
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import { Sort } from "@planzone/dto";

import { useModal } from "@root/client/modal";
import { ProjectSection } from "@common/projectSection";
import { Flag } from "@common/infiniteScroll";
import { PenIcon } from "@common/penIcon";
import { ZoneAvatar } from "@common/zoneAvatar";
import { TrashIcon } from "@common/trashIcon";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { ListToggleArrow } from "@common/listToggleArrow";
import { VisibilitySwitch } from "@common/visibilitySwitch";
import { isCustomCriteria, buildCustomSort } from "./sortFunctions";

import { Props } from "./types";
import { Loader } from "./loader";
import styles from "./styles.scss";

function Zone({
  zone,
  columnSizes,
  filter: f,
  sort: initialSort,
  resizeCol,
  resizeDone,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );

  const [currentSortValue, setCurrentSortValue] = useState<Sort>(initialSort);
  const [currentSortState, setCurrentSortState] = useState<{
    columnKey: string | null;
    direction: "asc" | "desc" | null;
  }>({
    columnKey: null,
    direction: null,
  });

  const filter = {
    ...f,
    zones: [zone],
  };

  const {
    index,
    projects,
    canRetrieveNextProjects,
    isRequestPending,
    retrieveProjects,
  } = useRetrieveProjects(filter, currentSortValue, 10);

  const get = () =>
    canRetrieveNextProjects &&
    !isRequestPending &&
    retrieveProjects({
      filter,
      sort: currentSortValue,
      index,
      size: 10,
    });

  const onColumnSortChange = useCallback(
    (columnKey: string, direction: "asc" | "desc" | null) => {
      if (!direction) {
        setCurrentSortValue(initialSort);
        setCurrentSortState({ columnKey: null, direction: null });
        retrieveProjects({
          filter,
          sort: initialSort,
          index: 0,
          size: 10,
        });
        return;
      }

      if (isCustomCriteria(columnKey, template)) {
        const customSort = buildCustomSort(columnKey, direction);
        setCurrentSortValue(customSort);
        setCurrentSortState({ columnKey, direction });
        retrieveProjects({
          filter,
          sort: customSort,
          index: 0,
          size: 10,
        });
      }
    },
    [filter, template, retrieveProjects, initialSort],
  );

  // A zone without any project for the filter, the default statuses one included, is hidden. Its own
  // list counts as well: a project created in it since the zones were listed shows it again.
  if (!zone.totalNumberOfProjectsInZone && projects.length === 0) return null;

  return (
    <>
      <VisibilitySwitch
        defaultState={true}
        renderChildren={(isVisible, show, hide) => (
          <ProjectSection
            {...zone}
            header={(filtered) => (
              <div className={styles.zoneInfo}>
                <div className={styles.row} onClick={!isVisible ? show : hide}>
                  <div className={styles.iconBox}>
                    <ListToggleArrow isOpen={isVisible} />
                  </div>
                  <div className={styles.avatar}>
                    <ZoneAvatar name={zone.name} color={zone.color} />
                  </div>
                  <h2 className={styles.name}>
                    {t("zonesPage.projects.zoneTitle", {
                      name: zone.name ?? "",
                      count: zone.totalNumberOfProjectsInZone ?? 0,
                    })}
                  </h2>
                </div>
                <div className={styles.contextualMenu}>
                  <SecureContextualMenu
                    items={[
                      {
                        name: t("zonesPage.projects.editZone"),
                        renderIcon: (className: string) => (
                          <PenIcon
                            className={classNames(className, styles.menuIcon)}
                          />
                        ),
                        useCase: UseCases.editZone,
                        act: () =>
                          pushView({
                            key: UseCases.editZone,
                            data: zone,
                          }),
                      },
                      {
                        name: t("zonesPage.projects.removeZone"),
                        color: "#DB5F63FF",
                        useCase: UseCases.removeZone,
                        renderIcon: (className: string) => (
                          <TrashIcon
                            className={classNames(className, styles.removeIcon)}
                          />
                        ),
                        act: () =>
                          pushView({
                            key: UseCases.removeZone,
                            data: zone,
                          }),
                      },
                    ]}
                  />
                </div>
              </div>
            )}
            columnSizes={columnSizes}
            projects={projects.map((p) => ({ ...p, zone }))}
            filter={filter}
            isOpen={isVisible}
            loader={isRequestPending ? <Loader /> : undefined}
            currentSort={currentSortState}
            onColumnSort={onColumnSortChange}
            resizeCol={resizeCol}
            resizeDone={resizeDone}
          />
        )}
      />
      <Flag
        name={zone.uuid}
        retrieveItems={get}
        isDisabled={!canRetrieveNextProjects || isRequestPending}
      />
    </>
  );
}

export { Zone };
